/* Copyright (c) 2020-present CloudIO Inc */

import { Theme, Direction, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { AnyColumn, ItemID } from 'componentregistry';
import React, {
  CSSProperties,
  forwardRef,
  memo,
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Suspender from 'utils/Suspender';
import useJoinRef from 'utils/useJoinRef';
import { isNull } from '../../../utils/isEmpty';
import { useGridBounds } from '../Context';
import Cells from './Cells';
import { useScrollValue } from './GridScroll';
import Scrollbar from './Scrollbar';
import { Cell, CellComponentProps, DataContext } from './types';
import { useStateXGetter, useStateXSetter } from '@cloudio-saas/statex';
import { dataGridSelectedData } from 'layout/Sidebar/atoms';
import { SimpleNoDataFound } from 'api/NoDataFound';
import WaveDots from 'utils/WaveDots';
import { usePasteProgress } from './PasteProgressContext';

const useStyles = makeStyles((theme: Theme) => ({
  gridBody: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    overflow: 'hidden',
    maxHeight: 'unset',
    maxWidth: 'unset',
  },
  gridBodyInner: {
    position: 'absolute',
    top: 0,
  },
  sideWrapper: {
    position: 'absolute',
    overflow: 'hidden',
    zIndex: 2,
  },
  side: {
    position: 'absolute',
    top: 0,
    overflow: 'hidden',
  },
}));

interface Props {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  roleUid: string;
  bodyHeight: number;
  columnLefts: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  fixedLeftColumns: number;
  fixedRightColumns: number;
  footerRows: number;
  headerRows: number;
  height: number;
  highlightCell: boolean;
  highlightRow: boolean;
  horizontalScrollbarSize: number;
  innerWidth: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  pageId: ItemID;
  noDataMsg?: string;
  showNoDataMsg?: boolean;
  showPasteProgress?: boolean;
  rowHeight: number;
  scrollToRow: number;
  scrollToColumn: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  totalColumns: number;
  totalRows: number;
  vertialScrollbarSize: number;
  width: number;
  bodyRef?: React.Ref<HTMLDivElement>;
  bodyProps?: React.HTMLAttributes<HTMLElement>;
  resetScroll: () => void;
  onRowDoubleClick?: (_rowIndex: number) => void;
}

const Body = forwardRef(
  (
    props: Props,
    ref:
      | React.Ref<HTMLDivElement>
      | React.MutableRefObject<HTMLDivElement | null>
      | null,
  ): React.ReactElement => {
    const classes = useStyles();
    const rootRef = useRef<HTMLDivElement | null>(null);

    const set = useStateXSetter();

    const get = useStateXGetter();

    const scrolling = false; // useIsScrolling();

    // useWhyDidYouUpdate('Footer', props);
    const { progress } = usePasteProgress();

    const {
      CellComponent,
      appUid,
      roleUid,
      bodyHeight,
      bodyProps,
      bodyRef,
      columnLefts,
      columnWidths,
      columns,
      custom,
      dataContext,
      direction,
      fixedLeftColumns,
      fixedRightColumns,
      footerRows,
      headerRows,
      height,
      highlightCell,
      highlightRow,
      horizontalScrollbarSize,
      innerWidth,
      onClick,
      onKeyDown,
      onRowDoubleClick,
      pageId,
      resetScroll,
      rowHeight,
      scrollToRow,
      scrollToColumn,
      showColumnLines,
      showRowLines,
      totalColumns,
      totalRows,
      vertialScrollbarSize,
      width,
      noDataMsg,
      showNoDataMsg,
      showPasteProgress,
    } = props;

    const leftRef = useRef<HTMLDivElement | null>(null);
    const rightRef = useRef<HTMLDivElement | null>(null);
    const parentVariableRef = useRef(
      new Map<string, string | undefined | null | number>(),
    );
    const joinRef = useJoinRef(rootRef, ref, bodyRef);

    const leftFixedWidth = fixedLeftColumns
      ? columnLefts[fixedLeftColumns - 1] + columnWidths[fixedLeftColumns - 1]
      : 0;
    const rightFixedWidth = fixedRightColumns
      ? innerWidth - columnLefts[totalColumns - fixedRightColumns]
      : 0;

    const headerHeight = headerRows ? headerRows * rowHeight : 0;
    const footerHeight = footerRows ? footerRows * rowHeight : 0;

    const mv = useScrollValue();

    const updateSelectedCells = (
      rowIndex: number,
      columnIndex: number,
      value: string | undefined | null | number,
    ) => {
      const newSelectedCells: Map<string, string | undefined | null | number> =
        new Map();
      const dataMap = get(dataGridSelectedData);
      newSelectedCells.set(`${rowIndex}${columnIndex}`, value);
      const newDataMap = new Map([...dataMap, ...newSelectedCells]);
      parentVariableRef.current = newDataMap;
      set(dataGridSelectedData, newDataMap);
    };
    useEffect(() => {
      return mv.pubsub.sub('OnChange', async (_event, { x, y }) => {
        const el = rootRef.current;
        if (el) {
          if (!isNull(x)) {
            // console.log('scrollTo', { x, y, left: el.scrollLeft });
            el.scrollLeft = direction === 'rtl' ? x : -x;
          }
          if (!isNull(y)) {
            // console.log('scrollTo', { x, y, top: el.scrollTop });
            el.scrollTop = -y;
            if (leftRef.current) {
              leftRef.current.scrollTop = -y;
            }
            if (rightRef.current) {
              rightRef.current.scrollTop = -y;
            }
          }
        }
      });
    }, [direction, mv.pubsub]);

    useLayoutEffect(() => {
      if (!vertialScrollbarSize) {
        if (rootRef.current) rootRef.current.scrollTop = 0;
        if (leftRef.current) leftRef.current.scrollTop = 0;
        if (rightRef.current) rightRef.current.scrollTop = 0;
      }
      if (!horizontalScrollbarSize) {
        if (rootRef.current) rootRef.current.scrollLeft = 0;
      }
    }, [vertialScrollbarSize, horizontalScrollbarSize]);

    const actualBodyHeight = (totalRows - headerRows - footerRows) * rowHeight;
    const h = Math.max(actualBodyHeight, height - headerHeight - footerHeight);

    const vstyle: CSSProperties = useMemo(
      () => ({
        width: vertialScrollbarSize,
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'absolute',
        zIndex: 4,
        top: headerHeight,
        height: bodyHeight - horizontalScrollbarSize,
        [direction === 'rtl' ? 'left' : 'right']: 0,
      }),
      [
        bodyHeight,
        direction,
        headerHeight,
        horizontalScrollbarSize,
        vertialScrollbarSize,
      ],
    );

    const hstyle: CSSProperties = useMemo(
      () => ({
        height: horizontalScrollbarSize,
        overflowY: 'hidden',
        overflowX: 'auto',
        position: 'absolute',
        zIndex: 4,
        bottom: footerHeight,
        [direction === 'rtl' ? 'right' : 'left']: 0,
        width,
      }),
      [direction, footerHeight, horizontalScrollbarSize, width],
    );
    const gridBoundsSignal = useGridBounds();
    const scrollLeftRef = useRef<number>(0);
    const [
      {
        left,
        startColumn,
        endColumn,
        endRow,
        startRow,
        actualEndRow,
        actualStartRow,
      },
      setState,
    ] = useState<{
      left: number;
      startColumn: number;
      endColumn: number;
      endRow: number;
      startRow: number;
      actualStartRow: number;
      actualEndRow: number;
    }>(() => {
      const bound = gridBoundsSignal.peek();
      scrollLeftRef.current = bound.pos.left;
      return {
        left: bound.pos.left,
        startColumn: bound.bound.column.start,
        endColumn: bound.bound.column.end,
        endRow: bound.bound.row.end,
        startRow: bound.bound.row.start,
        actualStartRow: bound.bound.actualRow.start,
        actualEndRow: bound.bound.actualRow.end,
      };
    });

    useEffect(() => {
      return gridBoundsSignal.pos.left.onChange((_left) => {
        scrollLeftRef.current = _left;
        startTransition(() => {
          setState((s) => (s.left === _left ? s : { ...s, left: _left }));
        });
      });
    }, [gridBoundsSignal]);
    useEffect(() => {
      return gridBoundsSignal.bound.onChange((bound) => {
        startTransition(() => {
          setState((s) => ({
            ...s,
            startColumn: bound.column.start,
            endColumn: bound.column.end,
            endRow: bound.row.end,
            startRow: bound.row.start,
            actualStartRow: bound.actualRow.start,
            actualEndRow: bound.actualRow.end,
          }));
        });
      });
    }, [gridBoundsSignal]);

    useEffect(() => {
      if (rootRef.current && rootRef.current.scrollLeft === 0) {
        rootRef.current.scrollLeft = scrollLeftRef.current;
      }
    }, [totalRows, scrollLeftRef]);
    // console.log('Body', { left, startColumn, endColumn, endRow, startRow });

    // useWhyDidYouUpdate('Body', {
    //   ...props,
    //   left,
    //   startColumn,
    //   endColumn,
    //   endRow,
    //   startRow,
    // });
    if (totalRows - headerRows - footerRows === 0 && showNoDataMsg)
      return (
        <>
          <Box
            {...bodyProps}
            className={clsx(bodyProps?.className)}
            ref={joinRef}
            sx={{
              ...bodyProps?.style,
              width: Math.min(window.innerWidth, width),
              height:
                height - headerHeight - footerHeight - horizontalScrollbarSize,
              [direction === 'rtl' ? 'right' : 'left']:
                columnLefts[fixedLeftColumns] || 0,
              top: headerHeight,
              direction,
              pointerEvents: scrolling ? 'none' : undefined,
            }}
          >
            <Box
              sx={{
                height: totalRows * rowHeight - headerHeight - footerHeight,
                width: Math.min(window.innerWidth, width),
                [direction === 'rtl' ? 'right' : 'left']: 0,
              }}
            >
              <SimpleNoDataFound msg={noDataMsg} />
            </Box>
          </Box>
        </>
      );
    return (
      <>
        {fixedLeftColumns > 0 ? (
          <Box
            ref={leftRef}
            className={clsx(
              'io-grid-body-left',
              classes.sideWrapper,
              left > 5 ? 'io-grid-left-fixed-wrapper-shadow' : null,
            )}
            sx={{
              height: bodyHeight,
              width: leftFixedWidth,
              top: headerHeight,
              [direction === 'rtl' ? 'right' : 'left']: 0,
            }}
          >
            <Box
              className={clsx('io-grid-body-left-inner', classes.side)}
              sx={{
                height: h,
                width: leftFixedWidth,
                [direction === 'rtl' ? 'right' : 'left']: 0,
                pointerEvents: scrolling ? 'none' : undefined,
              }}
            >
              <Cells
                CellComponent={CellComponent}
                appUid={appUid}
                columnLefts={columnLefts}
                columnWidths={columnWidths}
                columns={columns}
                custom={custom}
                dataContext={dataContext}
                direction={direction}
                endColumn={fixedLeftColumns - 1}
                endRow={endRow}
                fixedLeftColumns={fixedLeftColumns}
                headerRows={headerRows}
                highlightCell={highlightCell}
                highlightRow={highlightRow}
                key="body-left"
                onClick={onClick}
                onKeyDown={onKeyDown}
                onRowDoubleClick={onRowDoubleClick}
                pageId={pageId}
                region="body-left"
                resetScroll={resetScroll}
                roleUid={roleUid}
                rowHeight={rowHeight}
                showColumnLines={showColumnLines}
                showRowLines={showRowLines}
                startColumn={0}
                startRow={startRow}
                actualStartRow={actualStartRow}
                actualEndRow={actualEndRow}
                totalRows={totalRows}
                updateSelectedCells={updateSelectedCells}
              />
            </Box>
          </Box>
        ) : null}
        <Box
          {...bodyProps}
          className={clsx(
            'io-grid-body-main',
            bodyProps?.className,
            classes.gridBody,
          )}
          ref={joinRef}
          sx={{
            ...bodyProps?.style,
            width:
              width - leftFixedWidth - rightFixedWidth - vertialScrollbarSize,
            height:
              height - headerHeight - footerHeight - horizontalScrollbarSize,
            [direction === 'rtl' ? 'right' : 'left']:
              columnLefts[fixedLeftColumns] || 0,
            top: headerHeight,
            direction,
            pointerEvents: scrolling ? 'none' : undefined,
          }}
        >
          {progress.isPasting && showPasteProgress ? (
            <Box
              sx={{
                // alignSelf: 'center',
                // height: '100%',
                position: 'fixed',
                bottom: 20,
                right: 20,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '10px 15px',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              <WaveDots active />
              <Box sx={{ ml: 2 }}>
                Pasting records: {progress.current} of {progress.total}
              </Box>
            </Box>
          ) : (
            <></>
          )}
          <Box
            className={clsx('io-grid-body-main-inner', classes.gridBodyInner)}
            sx={{
              height: totalRows * rowHeight - headerHeight - footerHeight,
              width: innerWidth - leftFixedWidth - rightFixedWidth,
              [direction === 'rtl' ? 'right' : 'left']: 0,
              // transform: `translate(${
              //   direction === 'rtl' ? left : -left
              // }px, ${-top}px)`,
              // transition: 'transform 0.3s ease',
            }}
          >
            <Cells
              CellComponent={CellComponent}
              appUid={appUid}
              columnLefts={columnLefts}
              columnWidths={columnWidths}
              columns={columns}
              custom={custom}
              dataContext={dataContext}
              direction={direction}
              endColumn={endColumn}
              endRow={endRow}
              fixedLeftColumns={fixedLeftColumns}
              headerRows={headerRows}
              highlightCell={highlightCell}
              highlightRow={highlightRow}
              key="body-center"
              onClick={onClick}
              onKeyDown={onKeyDown}
              onRowDoubleClick={onRowDoubleClick}
              pageId={pageId}
              region="body-center"
              resetScroll={resetScroll}
              roleUid={roleUid}
              rowHeight={rowHeight}
              showColumnLines={showColumnLines}
              showRowLines={showRowLines}
              startColumn={startColumn}
              startRow={startRow}
              actualStartRow={actualStartRow}
              actualEndRow={actualEndRow}
              totalRows={totalRows}
              updateSelectedCells={updateSelectedCells}
            />
          </Box>
        </Box>

        {fixedRightColumns > 0 ? (
          <Box
            ref={rightRef}
            className={clsx(
              'io-grid-body-right',
              classes.sideWrapper,
              left <
                innerWidth -
                  leftFixedWidth -
                  rightFixedWidth -
                  (width - leftFixedWidth - rightFixedWidth) -
                  5
                ? 'io-grid-right-fixed-wrapper-shadow'
                : null,
            )}
            sx={{
              height: bodyHeight,
              width: rightFixedWidth,
              top: headerHeight,
              [direction === 'rtl' ? 'left' : 'right']: vertialScrollbarSize,
            }}
          >
            <Box
              className={clsx('io-grid-body-right-inner', classes.side)}
              sx={{
                height: Math.max(
                  (totalRows - headerRows) * rowHeight,
                  height - headerHeight - footerHeight,
                ),
                width: rightFixedWidth,
                [direction === 'rtl' ? 'right' : 'left']: 0,
                pointerEvents: scrolling ? 'none' : undefined,
              }}
            >
              <Cells
                CellComponent={CellComponent}
                appUid={appUid}
                roleUid={roleUid}
                columnWidths={columnWidths}
                columns={columns}
                custom={custom}
                dataContext={dataContext}
                direction={direction}
                endColumn={totalColumns - 1}
                endRow={endRow}
                fixedLeftColumns={fixedLeftColumns}
                headerRows={headerRows}
                highlightCell={highlightCell}
                highlightRow={highlightRow}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onRowDoubleClick={onRowDoubleClick}
                pageId={pageId}
                region="body-right"
                key="body-right"
                resetScroll={resetScroll}
                rowHeight={rowHeight}
                showColumnLines={showColumnLines}
                showRowLines={showRowLines}
                startColumn={totalColumns - fixedRightColumns}
                startRow={startRow}
                actualStartRow={actualStartRow}
                actualEndRow={actualEndRow}
                totalRows={totalRows}
                updateSelectedCells={updateSelectedCells}
              />
            </Box>
          </Box>
        ) : null}
        {vertialScrollbarSize ? (
          <Scrollbar
            appUid={appUid}
            bodyHeight={bodyHeight - horizontalScrollbarSize}
            bodyWidth={width}
            columnLefts={columnLefts}
            columnWidths={columnWidths}
            height={totalRows * rowHeight - headerHeight - footerHeight}
            horizontal={false}
            itemId={dataContext.itemId}
            pageId={pageId}
            rowHeight={rowHeight}
            scrollToColumn={scrollToColumn}
            scrollToRow={scrollToRow}
            style={vstyle}
            width={vertialScrollbarSize}
          />
        ) : null}
        {horizontalScrollbarSize ? (
          <Scrollbar
            appUid={appUid}
            bodyHeight={bodyHeight - horizontalScrollbarSize}
            bodyWidth={width}
            columnLefts={columnLefts}
            columnWidths={columnWidths}
            height={horizontalScrollbarSize}
            horizontal
            itemId={dataContext.itemId}
            pageId={pageId}
            rowHeight={rowHeight}
            scrollToColumn={scrollToColumn}
            scrollToRow={scrollToRow}
            style={hstyle}
            width={innerWidth}
          />
        ) : null}
        <Suspender name="GridBody" />
      </>
    );
  },
);

Body.displayName = 'Body';

export default memo(Body);
